import React, { useEffect, useState } from "react";
import classNames from "classnames";

import { dateToMonth } from "../lib/utils";
import MonthSection from "./MonthSection";
import DropdownIcon from "./ui/DropdownIcon";

const monthsNames = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

function Calendar({
  date1 = null,
  date2 = null,
  date1Title = "Заезд",
  date2Title = "Выезд",
  minDt = null,
  maxDt = null, // Новый пропс - максимальная допустимая дата
  onSelectDates,
  singleDateSelect = false,
  singleDateHighlightOnly = false, // Новый пропс - выделять только одну дату
  showSelectedDates = true, // показывать выбранные даты
  showPastMonths = false, // показывать прошлые месяцы
  allTime = false,
  onClose = null,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [currentMonth, setCurrentMonth] = useState(currentDate.getMonth());
  const [currentYear, setCurrentYear] = useState(currentDate.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(currentMonth);
  const [months, setMonths] = useState([]);

  const [beginDate, setBeginDate] = useState(date1);
  const [endDate, setEndDate] = useState(date2);
  const [minDate, setMinDate] = useState(minDt);

  useEffect(() => {
    setBeginDate(date1);
    setEndDate(date2);

    if (
      date1 &&
      (allTime ||
        showPastMonths ||
        date1 >= currentDate ||
        singleDateHighlightOnly)
    ) {
      setSelectedMonth(date1.getMonth());
      setCurrentMonth(date1.getMonth());
      setCurrentYear(date1.getFullYear());
    }

    if (allTime) {
      updateMonthsList(currentMonth, currentYear);
    } else {
      if (showPastMonths) {
        // Показываем 6 прошлых и 6 будущих месяцев
        updateMonthsList(currentDate.getMonth(), currentDate.getFullYear());
      } else {
        // Только будущие месяцы (как сейчас)
        generateFutureMonthsList(
          currentDate.getMonth(),
          currentDate.getFullYear(),
        );
      }
    }
  }, [date1, date2, allTime, showPastMonths, singleDateHighlightOnly]);

  const setBeginDateHandler = (dt) => {
    setBeginDate(dt);
    setEndDate(null);
  };

  const setEndDateHandler = (dt) => {
    setEndDate(dt);
    if (dt) {
      if (allTime) {
        updateMonthsList(currentMonth, currentYear);
      } else {
        setSelectedMonth(beginDate.getMonth());
      }
      onSelectDates([beginDate, dt]);
    }
  };

  const handleSingleDateSelect = (dt) => {
    setBeginDate(dt);
    setSelectedMonth(dt.getMonth()); // Добавлено
    if (allTime) {
      setCurrentMonth(dt.getMonth());
      setCurrentYear(dt.getFullYear());
      updateMonthsList(dt.getMonth(), dt.getFullYear());
    }
    onSelectDates([dt]);
  };

  const updateMonthsList = (centerMonth, year) => {
    const monthsList = [];
    const totalMonths = 12;
    const half = Math.floor(totalMonths / 2);

    // Определяем центр списка
    let centerMonthAdjusted = centerMonth;
    let centerYearAdjusted = year;

    // Если есть maxDt и singleDateHighlightOnly, центрируем вокруг выбранной даты
    if (maxDt && singleDateHighlightOnly && beginDate) {
      centerMonthAdjusted = beginDate.getMonth();
      centerYearAdjusted = beginDate.getFullYear();
    } else if (maxDt && showPastMonths) {
      // Иначе, если показываем прошлые месяцы, центрируем вокруг maxDt
      centerMonthAdjusted = maxDt.getMonth();
      centerYearAdjusted = maxDt.getFullYear();
    }

    for (let i = -half; i <= half; i++) {
      let month = centerMonthAdjusted + i;
      let monthYear = centerYearAdjusted;

      if (month < 0) {
        month += 12;
        monthYear -= 1;
      } else if (month > 11) {
        month -= 12;
        monthYear += 1;
      }

      // Проверяем границы minDt и maxDt
      const monthDate = new Date(monthYear, month, 1);
      if (maxDt && monthDate > maxDt) continue;
      if (
        minDt &&
        monthDate < new Date(minDt.getFullYear(), minDt.getMonth(), 1)
      )
        continue;

      monthsList.push({
        month: month,
        year: monthYear,
        name: monthsNames[month],
      });
    }

    setMonths(monthsList);
  };

  const generateFutureMonthsList = (startMonth, startYear) => {
    const monthsList = [];
    let currMonth = startMonth;
    let currYear = startYear;

    for (let i = 0; i < 12; i++) {
      monthsList.push({
        month: currMonth,
        year: currYear,
        name: monthsNames[currMonth],
      });
      currMonth++;
      if (currMonth > 11) {
        currMonth = 0;
        currYear++;
      }
    }

    setMonths(monthsList);
  };

  const changeMonth = (e) => {
    const newMonth = parseInt(e.target.dataset.month);
    const newYear = parseInt(e.target.dataset.year);
    setSelectedMonth(newMonth);
    setCurrentMonth(newMonth);
    setCurrentYear(newYear);
    if (allTime) {
      updateMonthsList(newMonth, newYear);
    }
  };

  const checkMonthIsActive = (month) => {
    if ((singleDateSelect || singleDateHighlightOnly) && beginDate) {
      // Только если месяц совпадает с выбранной датой
      return (
        month.year > beginDate.getFullYear() ||
        (month.month >= beginDate.getMonth() &&
          month.year >= beginDate.getFullYear())
      );
    }
    if (date1 && date2) {
      const monthDate = new Date(month.year, month.month, 31);
      const monthDate2 = new Date(month.year, month.month, 1);
      if (monthDate >= date1 && monthDate2 <= date2) {
        return true;
      }
    }
    return false;
  };

  return (
    <>
      {showSelectedDates && (
        <div className="calendarDatesSelectedWrap" onClick={onClose}>
          {singleDateSelect ? (
            <div className="calendarDatesSelected">
              {beginDate ? dateToMonth(beginDate) : "Дата"}
            </div>
          ) : (
            <div className="calendarDatesSelected">
              {beginDate ? dateToMonth(beginDate) : date1Title} -{" "}
              {endDate ? dateToMonth(endDate) : date2Title}
            </div>
          )}
          <DropdownIcon fill="#000" />
        </div>
      )}
      <div className={classNames("calendar", { singledate: singleDateSelect })}>
        <div className="calendar__months">
          <ul className="calendar__monthsList">
            {months.map((month, index) => {
              const isNewYear =
                index !== 0 && month.year !== months[index - 1].year;
              return (
                <React.Fragment key={`month${month.month}-${month.year}`}>
                  {isNewYear && (
                    <li className="yearHeader">
                      <div>{month.year}</div>
                    </li>
                  )}
                  <li
                    className={classNames("monthsList__item", {
                      current:
                        month.month == currentDate.getMonth() &&
                        currentYear == currentDate.getFullYear(),
                      active: month.month === selectedMonth,
                      inactive:
                        !allTime &&
                        new Date(month.year, month.month, 1) < currentDate,
                      selected: checkMonthIsActive(month),
                    })}
                    data-month={month.month}
                    data-year={month.year}
                    onClick={changeMonth}
                  >
                    {month.name}
                  </li>
                </React.Fragment>
              );
            })}
          </ul>
        </div>
        <div className="calendar__dates">
          <MonthSection
            currentMonth={currentMonth}
            currentYear={currentYear}
            monthName={monthsNames[currentMonth]}
            beginDate={beginDate}
            endDate={endDate}
            setBegin={setBeginDateHandler}
            setEnd={setEndDateHandler}
            minDate={minDt}
            maxDate={maxDt} // Передаем новый пропс
            singleDateSelect={singleDateSelect}
            singleDateHighlightOnly={singleDateHighlightOnly} // Передаем новый пропс
            handleSingleDateSelect={handleSingleDateSelect}
            allTime={allTime}
          />
          <MonthSection
            currentMonth={currentMonth + 1 > 11 ? 0 : currentMonth + 1}
            currentYear={currentMonth + 1 > 11 ? currentYear + 1 : currentYear}
            monthName={
              monthsNames[currentMonth + 1 > 11 ? 0 : currentMonth + 1]
            }
            beginDate={beginDate}
            endDate={endDate}
            minDate={minDt}
            maxDate={maxDt} // Передаем новый пропс
            singleDateSelect={singleDateSelect}
            singleDateHighlightOnly={singleDateHighlightOnly} // Передаем новый пропс
            allTime={allTime}
            setBegin={setBeginDateHandler}
            setEnd={setEndDateHandler}
            handleSingleDateSelect={handleSingleDateSelect}
          />
        </div>
      </div>
    </>
  );
}

export default Calendar;
